/**
 * The parsing half of a small, safe Markdown renderer for chat messages and
 * artifact documents: headings, paragraphs, lists, quotes, rules, GFM tables,
 * fenced code, inline code, bold, italic, ~~strikethrough~~, links and bare
 * URLs. Each platform renders the blocks with its own elements (the web:
 * `apps/web/src/lib/markdown.tsx`); raw HTML is never interpreted. Links
 * allow http(s) and mailto (`SAFE_URL`); @mentions follow `MENTION`.
 */
export type MdBlock =
  | { type: "heading"; level: number; text: string }
  | { type: "paragraph"; text: string }
  | { type: "code"; lang: string; text: string }
  | { type: "list"; ordered: boolean; items: string[] }
  | { type: "quote"; text: string; lines: string[] }
  | { type: "embed"; target: string }
  | { type: "rule" }
  | { type: "table"; header: string[]; rows: string[][]; align: TableAlign[] };

/** A column's alignment: from the separator (`:---`, `:---:`, `---:`), else right when every cell is a number. */
export type TableAlign = "left" | "center" | "right";

const LIST = /^\s*([-*+]|\d+[.)])\s+(.*)$/;
const splitRow = (line: string) =>
  line
    .trim()
    .replace(/^\||\|$/g, "")
    .split("|")
    .map((c) => c.trim());
const TABLE_RULE = /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)*\|?\s*$/;
// "429", "-17,2%", "~6,7%", "R$ 1.200", "$3.5k": numbers as people write them in a table.
const NUMERIC = /^[~≈<>+-]?\s*(R\$|US\$|\$|€)?\s*[+-]?\d[\d.,\s]*(%|k|mil|mi|x)?$/i;

/**
 * A GFM table starting at `lines[i]` (a row with pipes, then the `|---|` rule), or null. Shared by the
 * web and the phone app, so a table is a table anywhere in a message (also right under a sentence).
 */
export function tableAt(
  lines: readonly string[],
  i: number,
): { table: Extract<MdBlock, { type: "table" }>; next: number } | null {
  const line = lines[i] ?? "";
  const rule = lines[i + 1] ?? "";
  if (!line.includes("|") || !TABLE_RULE.test(rule)) return null;
  const header = splitRow(line);
  const marks = splitRow(rule);
  const rows: string[][] = [];
  let j = i + 2;
  while (j < lines.length && (lines[j] as string).includes("|") && (lines[j] as string).trim())
    rows.push(splitRow(lines[j++] as string));
  const align = header.map((_, c): TableAlign => {
    const m = marks[c] ?? "";
    if (m.startsWith(":") && m.endsWith(":")) return "center";
    if (m.endsWith(":")) return "right";
    if (m.startsWith(":")) return "left";
    const cells = rows.map((r) => (r[c] ?? "").replace(/[*_`]/g, "").trim()).filter(Boolean);
    return cells.length > 0 && cells.every((v) => NUMERIC.test(v)) ? "right" : "left";
  });
  return { table: { type: "table", header, rows, align }, next: j };
}

/** A block and the source lines it came from (`[first, end)`), for an editor that keeps untouched blocks as written. */
export type MdRangedBlock = MdBlock & { range: [number, number] };

export function parseMarkdown(source: string, options: { breaks?: boolean; ranges: true }): MdRangedBlock[];
export function parseMarkdown(source: string, options?: { breaks?: boolean; ranges?: false }): MdBlock[];
export function parseMarkdown(source: string, options: { breaks?: boolean; ranges?: boolean } = {}): MdBlock[] {
  const join = options.breaks ? "\n" : " ";
  const lines = source.replace(/\r\n?/g, "\n").split("\n");
  const out: MdBlock[] = [];
  let i = 0;
  let start = 0;
  // Each block is pushed once its lines are read (`i` is past them).
  const push = (b: MdBlock) => {
    const ranged: MdRangedBlock = { ...b, range: [start, i] };
    out.push(options.ranges ? ranged : b);
  };
  while (i < lines.length) {
    start = i;
    const line = lines[i] as string;
    const fence = line.match(/^\s*```\s*([\w+-]*)\s*$/);
    if (fence) {
      const body: string[] = [];
      i++;
      while (i < lines.length && !/^\s*```\s*$/.test(lines[i] as string)) body.push(lines[i++] as string);
      i = Math.min(i + 1, lines.length);
      push({ type: "code", lang: fence[1] ?? "", text: body.join("\n") });
      continue;
    }
    if (!line.trim()) {
      i++;
      continue;
    }
    const heading = line.match(/^(#{1,6})\s+(.*?)\s*#*\s*$/);
    if (heading) {
      i++;
      push({ type: "heading", level: (heading[1] as string).length, text: heading[2] ?? "" });
      continue;
    }
    if (/^\s*([-*_])(\s*\1){2,}\s*$/.test(line)) {
      i++;
      push({ type: "rule" });
      continue;
    }
    const table = tableAt(lines, i);
    if (table) {
      i = table.next;
      push(table.table);
      continue;
    }
    if (/^\s*>/.test(line)) {
      const body: string[] = [];
      while (i < lines.length && /^\s*>/.test(lines[i] as string))
        body.push((lines[i++] as string).replace(/^\s*>\s?/, ""));
      push({ type: "quote", text: body.join(join), lines: body });
      continue;
    }
    // `![[foto.png]]` alone on its line: an Obsidian embed (an attachment or another note).
    const embed = line.match(/^\s*!\[\[([^[\]\n|]+)(?:\|[^\]\n]*)?\]\]\s*$/);
    if (embed) {
      i++;
      push({ type: "embed", target: (embed[1] ?? "").trim() });
      continue;
    }
    const item = line.match(LIST);
    if (item) {
      const ordered = /\d/.test(item[1] as string);
      const items: string[] = [];
      while (i < lines.length) {
        const m = (lines[i] as string).match(LIST);
        if (m && /\d/.test(m[1] as string) === ordered) {
          items.push(m[2] ?? "");
          i++;
        } else if (items.length && /^\s{2,}\S/.test(lines[i] as string)) {
          items[items.length - 1] += `${join}${(lines[i++] as string).trim()}`;
        } else break;
      }
      push({ type: "list", ordered, items });
      continue;
    }
    const para: string[] = [];
    while (i < lines.length) {
      const l = lines[i] as string;
      if (!l.trim() || /^(#{1,6})\s|^\s*```|^\s*>/.test(l) || LIST.test(l) || (para.length && tableAt(lines, i))) break;
      para.push(l.trim());
      i++;
    }
    push({ type: "paragraph", text: para.join(join) });
  }
  return out;
}

// Code, bold, italic (not inside words, so `snake_case` and URLs stay as typed), links, bare URLs,
// wiki links, ~~strikethrough~~.
export const INLINE =
  /(`[^`]+`)|(\*\*[^*]+?\*\*|__[^_]+?__)|((?<![\w*])\*[^*\s][^*]*?\*(?![\w*])|(?<![\w_])_[^_\s][^_]*?_(?![\w_]))|(\[[^\]]+\]\([^)\s]+\))|(https?:\/\/[^\s<>()]*[^\s<>().,;:!?'"])|(\[\[[^[\]\n]+\]\])|(~~[^~\s](?:[^~]*?[^~\s])?~~)/;
export const SAFE_URL = /^(https?:|mailto:)/i;
/** Same rule as the backend (`parseMentionHandles`): emails are not mentions. */
export const MENTION = /(^|[^\w@.])@([a-zA-Z0-9][a-zA-Z0-9_-]{1,31})(?![\w@-])/g;

/** The first of `agents` a text @mentions, ignoring case as the backend does; undefined when it names none of them. */
export function firstMentioned<A extends { handle: string }>(text: string, agents: readonly A[]): A | undefined {
  for (const m of text.matchAll(MENTION)) {
    const handle = m[2]?.toLowerCase();
    const agent = agents.find((a) => a.handle.toLowerCase() === handle);
    if (agent) return agent;
  }
  return undefined;
}

/** Whether a text @mentions `handle`, ignoring case as the backend does. */
export function mentionsHandle(text: string, handle: string): boolean {
  const wanted = handle.toLowerCase();
  for (const m of text.matchAll(MENTION)) if (m[2]?.toLowerCase() === wanted) return true;
  return false;
}

/** `[[Processos/Onboarding|o onboarding]]` → { title: "Processos/Onboarding", label: "o onboarding" }. */
export function parseWikiToken(token: string) {
  const inner = token.slice(2, -2);
  const bar = inner.indexOf("|");
  const title = (bar >= 0 ? inner.slice(0, bar) : inner).trim();
  const label = (bar >= 0 ? inner.slice(bar + 1) : inner).trim() || title;
  return { title, label };
}

/** Every `[[link]]` title in a text, in order, without repeats. */
export function wikiLinksIn(text: string): string[] {
  const out: string[] = [];
  for (const m of text.matchAll(/(?<!!)\[\[([^[\]\n]+)\]\]/g)) {
    const { title } = parseWikiToken(m[0]);
    if (title && !out.includes(title)) out.push(title);
  }
  return out;
}

const INLINE_ALL = new RegExp(INLINE.source, "g");

/** A line's words without its inline marks: links and wiki links as their labels, an image as its alt text. */
const plainInline = (text: string): string =>
  text
    .replace(/!(?=\[)/g, "")
    .replace(INLINE_ALL, (tok, code, bold, em, link, _url, wiki, strike) =>
      code
        ? tok.slice(1, -1)
        : bold || strike
          ? plainInline(tok.slice(2, -2))
          : em
            ? plainInline(tok.slice(1, -1))
            : link
              ? plainInline(tok.slice(1, tok.indexOf("](")))
              : wiki
                ? parseWikiToken(tok).label
                : tok,
    );

/**
 * A Markdown text's prose as plain lines, the way a person reads it: each line of its paragraphs, list items
 * and quotes, without their marks. Headings, rules, code, tables and embeds are left out: they frame what it
 * says rather than say it.
 */
export function plainLines(source: string): string[] {
  return parseMarkdown(source, { breaks: true })
    .flatMap((b) =>
      b.type === "paragraph" ? [b.text] : b.type === "list" ? b.items : b.type === "quote" ? b.lines : [],
    )
    .flatMap((text) => text.split("\n"))
    .map((line) => plainInline(line).replace(/\s+/g, " ").trim())
    .filter(Boolean);
}

/** A text with a code block or a table: it needs the conversation's whole width, never a bubble's. */
export const hasWideBlock = (source: string) =>
  /```|\|/.test(source) && parseMarkdown(source).some((b) => b.type === "code" || b.type === "table");

/** Heading anchor ids for the outline: "Como pedimos acesso" → "como-pedimos-acesso". */
export const headingId = (text: string) =>
  text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
